import React, { useState } from 'react';
import { FileText, Image as ImageIcon, Trash2, UploadCloud } from 'lucide-react';
import { Badge, Button, Card, ConfirmationModal, toast } from '../../components/v2/V2Components';
import MortgageService from '../../services/MortgageService';
import BulkEscrowImport from './BulkEscrowImport';
import { formatCurrency, formatDate, isImageFile } from '../../utils/billFormat';
import {
  categoryShortfall,
  escrowHistory,
  formatCycle,
  latestDisclosure,
  sortDisclosuresNewestFirst,
  toMoney,
} from '../../utils/escrowDisclosures';

const CATEGORY_LABELS = {
  property_taxes: 'Property taxes',
  homeowners_insurance: 'Homeowners insurance',
  mortgage_insurance: 'Mortgage insurance',
  other: 'Other',
};

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

const formatChange = (change) => {
  if (change == null) return '—';
  if (change === 0) return 'No change';
  return `${change > 0 ? '+' : '−'}${formatCurrency(Math.abs(change))}`;
};

// `min-w-0` + `break-words`: a grid track can't shrink below an unbreakable
// currency string without them (same reason as MortgageDetails' Spec).
const Figure = ({ label, value, detail }) => (
  <div className="min-w-0">
    <div className="text-[var(--text-muted)] text-xs uppercase break-words">{label}</div>
    <div className="font-semibold text-[var(--text-main)] break-words">{value}</div>
    {detail && <div className="text-xs text-[var(--text-secondary)] break-words">{detail}</div>}
  </div>
);

const balanceFigure = (disclosure) => {
  const shortage = toMoney(disclosure.shortage_amount);
  const surplus = toMoney(disclosure.surplus_amount);
  if (shortage > 0) return { label: 'Shortage', value: formatCurrency(shortage) };
  if (surplus > 0) return { label: 'Surplus', value: formatCurrency(surplus) };
  if (shortage === 0 || surplus === 0) return { label: 'Shortage', value: 'None' };
  return { label: 'Shortage / surplus', value: '—' };
};

const LatestSummary = ({ disclosure }) => {
  const previousEscrow = toMoney(disclosure.previous_escrow_payment);
  const previousTotal = toMoney(disclosure.previous_total_payment);
  const balance = balanceFigure(disclosure);
  const shortfall = categoryShortfall(disclosure);
  const lines = Array.isArray(disclosure.disbursements) ? disclosure.disbursements : [];
  const categories = [
    ['Property taxes', disclosure.property_taxes_annual],
    ['Homeowners insurance', disclosure.homeowners_insurance_annual],
    ['Mortgage insurance', disclosure.mortgage_insurance_annual],
    ['Other', disclosure.other_annual],
  ].filter(([, value]) => toMoney(value) != null);

  return (
    <section aria-label="Latest escrow disclosure" className="mb-5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 mb-3">
        <h3 className="text-xs font-semibold uppercase text-[var(--text-secondary)]">
          {formatCycle(disclosure.cycle_start)}
        </h3>
        {disclosure.statement_date && (
          <span className="text-xs text-[var(--text-muted)]">Analysis dated {formatDate(disclosure.statement_date)}</span>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <Figure
          label="Escrow / month"
          value={formatCurrency(disclosure.new_escrow_payment)}
          detail={previousEscrow != null ? `was ${formatCurrency(previousEscrow)}` : null}
        />
        <Figure
          label="Total payment"
          value={formatCurrency(disclosure.new_total_payment)}
          detail={previousTotal != null ? `was ${formatCurrency(previousTotal)}` : null}
        />
        <Figure label={balance.label} value={balance.value} />
        <Figure label="Required cushion" value={formatCurrency(disclosure.required_cushion)} />
      </div>

      {(categories.length > 0 || toMoney(disclosure.total_annual_disbursements) != null) && (
        <div className="mt-4 p-3 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)]">
          <h4 className="text-xs font-semibold uppercase text-[var(--text-secondary)] mb-2">Projected for the year</h4>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
            {categories.map(([label, value]) => (
              <div key={label} className="flex justify-between gap-3 min-w-0">
                <dt className="text-[var(--text-secondary)] min-w-0 break-words">{label}</dt>
                <dd className="text-[var(--text-main)] whitespace-nowrap">{formatCurrency(value)}</dd>
              </div>
            ))}
            {toMoney(disclosure.total_annual_disbursements) != null && (
              <div className="flex justify-between gap-3 min-w-0 font-semibold sm:col-span-2 border-t border-[var(--border)] pt-1 mt-1">
                <dt className="text-[var(--text-main)]">Total</dt>
                <dd className="text-[var(--text-main)] whitespace-nowrap">
                  {formatCurrency(disclosure.total_annual_disbursements)}
                </dd>
              </div>
            )}
          </dl>
          {shortfall != null && (
            <p className="text-xs text-[var(--text-muted)] mt-2">
              The categories come to {formatCurrency(Math.abs(shortfall))} {shortfall > 0 ? 'less' : 'more'} than the
              servicer&apos;s total — a payment may have been missed or double-counted. Check the PDF.
            </p>
          )}
          {lines.length > 0 && (
            <details className="mt-2">
              <summary className="text-xs text-[var(--primary)] cursor-pointer">
                {plural(lines.length, 'line item')}
              </summary>
              <ul className="mt-2 space-y-1 text-xs">
                {lines.map((line, index) => (
                  // Payees repeat (one per installment), so the index is part of the key.
                  <li key={`${line.description}-${index}`} className="flex justify-between gap-3 min-w-0">
                    <span className="text-[var(--text-secondary)] truncate">
                      {line.description}
                      <span className="text-[var(--text-muted)]"> · {CATEGORY_LABELS[line.category] || 'Other'}</span>
                    </span>
                    <span className="text-[var(--text-main)] whitespace-nowrap">{formatCurrency(line.annual_amount)}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </section>
  );
};

const HistoryRow = ({ row, onDelete, hideActions }) => {
  const { disclosure } = row;
  const filename = disclosure.original_filename || disclosure.filename || '';
  const [cycleStart, cycleEnd] = row.cycleLabel.split(' – ');
  return (
    <tr className="border-b border-[var(--border)] last:border-0">
      {/* Phones get the start month only, so the money columns fit beside it. */}
      <td className="py-3 px-2 text-sm text-[var(--text-main)] whitespace-nowrap">
        {cycleStart}
        {cycleEnd && <span className="hidden sm:inline"> – {cycleEnd}</span>}
      </td>
      <td className="py-3 px-2 text-sm font-semibold text-[var(--text-main)]">{formatCurrency(row.escrow)}</td>
      <td className="py-3 px-2 text-sm text-[var(--text-secondary)] whitespace-nowrap">{formatChange(row.change)}</td>
      <td className="py-3 px-2 text-sm text-[var(--text-secondary)]">
        {formatCurrency(disclosure.property_taxes_annual)}
      </td>
      <td className="py-3 px-2 text-sm text-[var(--text-secondary)]">
        {formatCurrency(disclosure.homeowners_insurance_annual)}
      </td>
      <td className="py-3 px-2 text-sm text-[var(--text-secondary)]">
        {formatCurrency(disclosure.mortgage_insurance_annual)}
      </td>
      {/* File and delete share one cell: separate columns pushed the table
          past the card's width on desktop. */}
      <td className="py-3 px-2 text-right whitespace-nowrap">
        {disclosure.url && (
          <a
            href={disclosure.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center p-2 rounded-lg text-[var(--primary)] hover:bg-[var(--bg-hover)]"
            aria-label={`Open the disclosure for ${row.cycleLabel}`}
            title="Open file"
          >
            {isImageFile(filename) ? <ImageIcon size={16} /> : <FileText size={16} />}
          </a>
        )}
        {/* Hidden while importing: deleting mid-run would race the per-file merge. */}
        {!hideActions && (
          <button
            type="button"
            onClick={() => onDelete(disclosure)}
            className="p-2 rounded-lg text-[var(--text-muted)] hover:text-[var(--error)] hover:bg-[var(--bg-hover)]"
            aria-label={`Delete the disclosure for ${row.cycleLabel}`}
            title="Delete disclosure"
          >
            <Trash2 size={16} />
          </button>
        )}
      </td>
    </tr>
  );
};

/**
 * The mortgage page's Escrow card (ADR-0013): the latest annual escrow
 * disclosure in full, and one history row per escrow cycle on file.
 * `onDisclosuresChange` takes a state updater, like a useState setter.
 */
const MortgageEscrowCard = ({ mortgage, disclosures, loadError = null, onDisclosuresChange }) => {
  // null | 'import' — the inline bulk-import panel.
  const [panel, setPanel] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const latest = latestDisclosure(disclosures);
  const history = escrowHistory(disclosures);

  // Merged per file as the import lands; deduped on id because a fixed
  // needs-attention file arrives through this same path.
  const handleCreated = (created) => {
    if (!created?.length) return;
    onDisclosuresChange((prev) => {
      const known = new Set(prev.map((d) => d.id));
      const fresh = created.filter((d) => d && !known.has(d.id));
      return fresh.length ? sortDisclosuresNewestFirst([...fresh, ...prev]) : prev;
    });
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      setDeleting(true);
      await MortgageService.deleteEscrowDisclosure(confirmDelete.id);
      onDisclosuresChange((prev) => prev.filter((d) => d.id !== confirmDelete.id));
      setConfirmDelete(null);
      toast.success('Disclosure deleted');
    } catch (err) {
      toast.error(err.message || 'Failed to delete disclosure');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Card className="p-5 mt-6">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mb-4">
          <div className="flex flex-wrap items-center gap-2 min-w-0">
            <h2 className="text-sm font-bold text-[var(--text-main)] uppercase tracking-wide">Escrow</h2>
            <Badge status={plural(disclosures.length, 'disclosure')} />
          </div>
          {panel === null && (
            <Button
              variant="secondary"
              leadingIcon={UploadCloud}
              className="w-full md:w-auto"
              onClick={() => setPanel('import')}
            >
              Add Disclosures
            </Button>
          )}
        </div>

        {panel === 'import' && (
          <BulkEscrowImport
            mortgageId={mortgage.id}
            onDisclosuresCreated={handleCreated}
            onClose={() => setPanel(null)}
          />
        )}

        {loadError && (
          <p className="text-sm text-[var(--error)] mb-3" role="alert">
            {loadError}
          </p>
        )}

        {latest ? (
          <LatestSummary disclosure={latest} />
        ) : (
          !loadError && (
            <div className="text-center py-6 text-sm text-[var(--text-muted)]">
              No escrow disclosures yet. Add your servicer&apos;s annual escrow statements to see your escrow payment,
              taxes and insurance year by year.
            </div>
          )
        )}

        {history.length > 0 && (
          <div>
            <h3 className="text-xs font-semibold uppercase text-[var(--text-secondary)] mb-2">History</h3>
            {/* `relative` is load-bearing: the header's sr-only label is
                absolutely positioned, and without a positioned ancestor inside
                the scroller it escapes the overflow clip and widens the whole
                page on a phone (measured: a 340px viewport scrolled to 604px). */}
            <div className="relative overflow-x-auto">
              <table aria-label="Escrow history" className="w-full">
                <thead>
                  <tr className="text-left text-xs uppercase text-[var(--text-secondary)] border-b border-[var(--border)]">
                    <th className="py-2 px-2 font-semibold">Cycle</th>
                    <th className="py-2 px-2 font-semibold">Escrow / mo</th>
                    <th className="py-2 px-2 font-semibold">Change</th>
                    <th className="py-2 px-2 font-semibold">Taxes</th>
                    <th className="py-2 px-2 font-semibold">Insurance</th>
                    <th className="py-2 px-2 font-semibold">Mortgage ins.</th>
                    <th className="py-2 px-2">
                      <span className="sr-only">File and actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((row) => (
                    <HistoryRow
                      key={row.disclosure.id}
                      row={row}
                      onDelete={setConfirmDelete}
                      hideActions={panel !== null}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Card>

      <ConfirmationModal
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
        title="Delete Disclosure?"
        message={`This will permanently delete the escrow disclosure for ${
          confirmDelete ? formatCycle(confirmDelete.cycle_start) : ''
        } and its attached file. This cannot be undone.`}
        confirmText="Delete"
        isDanger
        isLoading={deleting}
      />
    </>
  );
};

export default MortgageEscrowCard;
